import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, Loader2, ArrowLeft, ClipboardList } from 'lucide-react';
import { TrialSubmissionCard } from '../../components/TrialSubmissions/TrialSubmissionCard';
import { trialService, Trial } from '../../services/trialService';
import { useToast, ToastWrapper, useConfirm, ConfirmWrapper } from '../../components/UI/Toast';

type TabType = 'ALL' | 'ACCEPTED' | 'SUBMITTED' | 'PASSED' | 'FAILED';
type ViewModeType = 'client' | 'creator';

const TrialSubmissions: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const urlViewMode = searchParams.get('view') as ViewModeType | null;
    const initialViewMode: ViewModeType = urlViewMode === 'creator' || urlViewMode === 'client' ? urlViewMode : 'creator';

    const [activeTab, setActiveTab] = useState<TabType>('ALL');
    const [viewMode, setViewMode] = useState<ViewModeType>(initialViewMode);
    const [trials, setTrials] = useState<Trial[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const tabs: { key: TabType; label: string }[] = [
        { key: 'ALL', label: '全部' },
        { key: 'ACCEPTED', label: '待交付' },
        { key: 'SUBMITTED', label: '待评审' },
        { key: 'PASSED', label: '已通过' },
        { key: 'FAILED', label: '未通过' }
    ];

    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        if (viewMode !== params.get('view')) {
            params.set('view', viewMode);
            navigate(`/trials?${params.toString()}`, { replace: true });
        }
    }, [viewMode, navigate, searchParams]);

    useEffect(() => {
        const fetchTrials = async () => {
            setLoading(true);
            try {
                const res = await trialService.getTrials({ status: activeTab, viewMode });
                if (res.success && res.data?.trials) {
                    setTrials(res.data.trials);
                }
            } catch (error) {
                console.error('Failed to load trials:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTrials();
    }, [activeTab, viewMode]);

    const filteredTrials = trials.filter(trial =>
        trial.briefTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trial.clientName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const { showToast } = useToast();
    const { confirm } = useConfirm();

    const handleCancelTrial = async (e: React.MouseEvent, trialId: string) => {
        e.stopPropagation();
        
        const result = await confirm({
            title: '取消确认',
            message: '确认要取消该试稿吗？取消后试稿记录将被删除。',
            confirmText: '确认取消',
            cancelText: '点错了',
            tone: 'danger'
        });

        if (result.action === 'confirm') {
            try {
                const res = await trialService.deleteTrial(trialId);
                if (res.success) {
                    showToast('试稿已成功取消', 'success');
                    setTrials(prev => prev.filter(t => t.id !== trialId));
                } else {
                    showToast('取消试稿失败，请重试', 'error');
                }
            } catch (error) {
                console.error(error);
                showToast('取消试稿失败，请重试', 'error');
            }
        }
    };

    return (
        <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
            <ToastWrapper />
            <ConfirmWrapper />
            <div className="mx-auto max-w-[1440px] px-6 md:px-12">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                    <div>
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-4 group w-fit"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                            <span className="text-sm font-medium">返回</span>
                        </button>
                        <h1 className="text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3 mb-2">
                            <ClipboardList className="w-10 h-10 text-blue-600" />
                            我的试稿
                        </h1>
                    </div>

                    <div className="flex flex-col items-end gap-4">
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="搜索试稿需求或客户..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all text-sm outline-none"
                            />
                        </div>
                    </div>
                </div>

                <div className="mb-8 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="inline-flex bg-slate-100 rounded-xl p-1">
                        <button
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                                viewMode === 'creator' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                            }`}
                            onClick={() => setViewMode('creator')}
                        >
                            我是创作者
                        </button>
                        <button
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                                viewMode === 'client' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                            }`}
                            onClick={() => setViewMode('client')}
                        >
                            我是需求方
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 bg-gray-50/50 p-1.5 rounded-[2rem] w-fit xl:ml-auto border border-gray-100">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                                    activeTab === tab.key
                                        ? 'bg-white text-blue-600 shadow-md ring-1 ring-black/5'
                                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Section */}
                {loading ? (
                    <div className="py-32 flex flex-col items-center justify-center gap-4 text-gray-400">
                        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
                        <p className="font-medium">正在获取您的试稿信息...</p>
                    </div>
                ) : filteredTrials.length === 0 ? (
                    <div className="py-32 bg-white rounded-[3rem] border border-dashed border-gray-200 flex flex-col items-center justify-center text-center px-6">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                            <Filter className="w-8 h-8 text-gray-300" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">暂无试稿记录</h3>
                        <p className="text-gray-500 max-w-sm">
                            {searchQuery ? `未找到匹配 "${searchQuery}" 的试稿。` : `目前还没有该状态下的试稿。`}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-slide-up">
                        {filteredTrials.map((trial) => (
                            <TrialSubmissionCard
                                key={trial.id}
                                trial={trial}
                                onClick={() => navigate(`/trials/${trial.id}?role=${viewMode}`)}
                                onCancel={(e) => handleCancelTrial(e, trial.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrialSubmissions;
